import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	Box,
	Divider,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo, useRef, useState } from "react";
import { DelayByShift } from "../models/DelayByShift";

export const DelaysDailyTrend = ({
	delays,
	sectionName,
}: {
	delays: DelayByShift[];
	sectionName?: string | null;
}) => {
	const chartRef = useRef<ReactECharts>(null);
	const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

	const dailyData = useMemo(() => {
		const dateMap = new Map<string, { duration: number; count: number }>();

		for (const delay of delays) {
			const dateKey = dxtToLocalServerTime(delay.Start!, "yyyy-MM-dd");
			const existing = dateMap.get(dateKey);
			if (existing) {
				existing.duration += delay.Duration || 0;
				existing.count += 1;
			} else {
				dateMap.set(dateKey, { duration: delay.Duration || 0, count: 1 });
			}
		}

		return Array.from(dateMap.entries())
			.map(([dateStr, data]) => ({
				date: dateStr,
				durationSec: data.duration,
				durationMin: data.duration / 60,
				durationHrs: data.duration / 3600,
				eventCount: data.count,
			}))
			.sort((a, b) => a.date.localeCompare(b.date));
	}, [delays]);

	const handleChartEvents = {
		mouseover: (params: { dataIndex: number }) =>
			setHighlightedIndex(params.dataIndex),
		mouseout: () => setHighlightedIndex(null),
	};

	const handleTableRowHover = (index: number | null) => {
		setHighlightedIndex(index);
		const chartInstance = chartRef.current?.getEchartsInstance();
		if (chartInstance) {
			if (index !== null) {
				chartInstance.dispatchAction({
					type: "highlight",
					seriesIndex: 0,
					dataIndex: index,
				});
				chartInstance.dispatchAction({
					type: "showTip",
					seriesIndex: 0,
					dataIndex: index,
				});
			} else {
				chartInstance.dispatchAction({ type: "downplay", seriesIndex: 0 });
				chartInstance.dispatchAction({ type: "hideTip" });
			}
		}
	};

	const chartOption = {
		title: {
			text: "Daily Delay Duration (minutes)",
			left: "center",
			textStyle: { fontSize: 13, fontWeight: "bold" },
		},
		tooltip: {
			trigger: "axis",
			axisPointer: { type: "shadow" },
			formatter: (
				params: { name: string; value: number; dataIndex: number }[],
			) => {
				const p = params[0];
				return `${p.name}<br/>Duration: ${p.value} min<br/>Events: ${dailyData[p.dataIndex].eventCount}`;
			},
		},
		grid: { left: "3%", right: "4%", bottom: "15%", containLabel: true },
		xAxis: {
			type: "category",
			data: dailyData.map((d) => d.date.split("-").slice(1).join("/")),
			name: "Date",
			nameLocation: "middle",
			nameGap: 35,
		},
		yAxis: {
			type: "value",
			name: "Minutes",
			nameLocation: "middle",
			nameGap: 50,
		},
		series: [
			{
				name: "Duration (min)",
				type: "bar",
				data: dailyData.map((d) => Math.round(d.durationMin * 10) / 10),
				itemStyle: { color: "#e67e22" },
				barWidth: "60%",
				emphasis: { itemStyle: { color: "#d35400" } },
			},
		],
	};

	return (
		<Paper variant="outlined" sx={{ mb: 1 }}>
			<Box
				sx={{
					px: 2,
					py: 1,
					backgroundColor: (theme) => theme.palette.primary.main,
				}}
			>
				<Typography variant="subtitle2" sx={{ color: "white" }}>
					{sectionName ? `${sectionName} — ` : ""}Daily Delay Trend
				</Typography>
			</Box>

			<Divider />

			<Box sx={{ display: "flex" }}>
				<Box
					sx={{
						flex: 4,
						borderRight: "1px solid",
						borderColor: "divider",
						overflow: "hidden",
					}}
				>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell rowSpan={2}>Date</TableCell>
								<TableCell
									colSpan={3}
									align="center"
									sx={{ borderBottom: "none" }}
								>
									Duration
								</TableCell>
								<TableCell rowSpan={2} align="right">
									# Events
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell
									align="right"
									sx={{
										color: "text.secondary",
										fontSize: "0.7rem",
										borderTop: "1px #d0d0d0 solid",
									}}
								>
									seconds
								</TableCell>
								<TableCell
									align="right"
									sx={{
										color: "text.secondary",
										fontSize: "0.7rem",
										borderTop: "1px #d0d0d0 solid",
									}}
								>
									minutes
								</TableCell>
								<TableCell
									align="right"
									sx={{
										color: "text.secondary",
										fontSize: "0.7rem",
										borderTop: "1px #d0d0d0 solid",
									}}
								>
									hours
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{dailyData.map((row, index) => (
								<TableRow
									key={row.date}
									hover
									onMouseEnter={() => handleTableRowHover(index)}
									onMouseLeave={() => handleTableRowHover(null)}
									sx={{
										cursor: "pointer",
										...(highlightedIndex === index
											? { backgroundColor: "action.selected" }
											: {}),
									}}
								>
									<TableCell>
										{row.date.split("-").slice(1).join("/")}
									</TableCell>
									<TableCell align="right">
										{row.durationSec.toFixed(0)}
									</TableCell>
									<TableCell align="right">
										{row.durationMin.toFixed(1)}
									</TableCell>
									<TableCell align="right">
										{row.durationHrs.toFixed(2)}
									</TableCell>
									<TableCell align="right">{row.eventCount}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>

				<Box sx={{ flex: 6, minHeight: 350, p: 1 }}>
					<ReactECharts
						ref={chartRef}
						option={chartOption}
						onEvents={handleChartEvents}
						style={{ height: "100%", minHeight: 350 }}
					/>
				</Box>
			</Box>
		</Paper>
	);
};
